import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import type { SvgIconComponent } from '@mui/icons-material'

export interface DocumentItem {
    text: string
    Icon: SvgIconComponent
}

interface DocumentChecklistProps {
    items: DocumentItem[]
    /** Fees and processing notes the API attaches to the same section. */
    footnotes: string[]
    isMobile: boolean
}

const DocumentChecklist: React.FC<DocumentChecklistProps> = ({ items, footnotes, isMobile }) => {
    return (
        <Stack spacing={2}>
            <Box
                component="ul"
                sx={{
                    m: 0,
                    p: 0,
                    listStyle: 'none',
                    display: 'grid',
                    gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                    gap: isMobile ? 1 : 1.5
                }}
            >
                {items.map(({ text, Icon }) => (
                    <Stack
                        key={text}
                        component="li"
                        direction="row"
                        spacing={1.5}
                        sx={{
                            alignItems: 'flex-start',
                            p: isMobile ? 1.25 : 1.5,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'borders.default',
                            bgcolor: 'neutral.100'
                        }}
                    >
                        <Box
                            sx={{
                                width: 32,
                                height: 32,
                                flexShrink: 0,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: 'background.paper',
                                color: 'primary.main'
                            }}
                        >
                            <Icon sx={{ fontSize: 18 }} />
                        </Box>

                        <Typography
                            sx={{
                                fontSize: isMobile ? '1rem' : '0.875rem',
                                lineHeight: 1.55,
                                color: 'textColors.subtitle',
                                pt: 0.5
                            }}
                        >
                            {text}
                        </Typography>
                    </Stack>
                ))}
            </Box>

            {footnotes.length > 0 && (
                <Stack spacing={1} sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'borders.default' }}>
                    {footnotes.map(footnote => (
                        <Typography
                            key={footnote}
                            sx={{ fontSize: '0.75rem', lineHeight: 1.7, color: 'textColors.subheading' }}
                        >
                            {footnote}
                        </Typography>
                    ))}
                </Stack>
            )}
        </Stack>
    )
}

export { DocumentChecklist }
export default DocumentChecklist
